import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getChatListing} from "api/chat.api.ts";
import {getMessages} from "services/firestore/getMessages";
import {convertTimestamp} from "services/utils/convertTimestamp";
import {markMessagesAsRead} from "services/firestore/markMessagesAsRead";
import {updateTypingStatus} from "services/database/updateTypingStatus";
import {useAuth} from "context/AuthContext";
import {getOrCreateConversation} from "services/firestore/getOrCreateConversation";
import {sendMessage} from "services/firestore/sendMessage";

export const useChat = (currentUserId, role, activeId) => {
    const [activeConversationId, setActiveConversationId] = useState(null);
    const [activeChatData, setActiveChatData] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [isLoadingChat, setIsLoadingChat] = useState(false);
    const {userData, userRole} = useAuth();
    const queryClient = useQueryClient();

    const {data, isLoading} = useQuery({
        queryKey: ["getChatListing", role, currentUserId, activeId],
        queryFn: () => getChatListing(role, activeId),
        enabled: !!role,
    });

    const chatList = data?.data;

    const activeConversation = activeChatData;

    const isValidChat = (chat) => {
        if (!chat) return false;

        const hasValidUserData =
            chat.userData &&
            (chat.userData.first_name ||
                chat.userData.last_name ||
                chat.userData.teacher_user_id);

        const hasValidParticipants =
            chat.participants &&
            Object.keys(chat.participants).length > 0 &&
            Object.values(chat.participants).some((id) => id && id !== 0);

        return hasValidUserData && hasValidParticipants;
    };

    const handleSelectConversation = (chat) => {
        if (!isValidChat(chat)) {
            setActiveChatData(null);
            setActiveConversationId(null);
            setChatMessages([]);
            return;
        }

        setActiveChatData(chat);

        if (chat.firestore_conversation_id) {
            setActiveConversationId(chat.firestore_conversation_id);
        } else {
            setActiveConversationId(null);
            setChatMessages([]);
        }
    };

    const handleSendMessage = async (messageText) => {
        if (!activeChatData || !isValidChat(activeChatData)) {
            console.error("No valid active chat selected");
            return;
        }

        let conversationId = activeConversationId;

        const recipientId = Object.values(activeChatData.participants || {})
            .map(String)
            .find((p) => p && p !== String(currentUserId));

        if (!recipientId) {
            console.error("No recipient found");
            return;
        }

        if (!conversationId) {
            const recipientData = activeChatData.userData || {};
            try {
                conversationId = await getOrCreateConversation(
                    String(currentUserId ?? ""),
                    `${userData?.first_name ?? ""} ${userData?.last_name ?? ""}`.trim(),
                    userRole === 1 ? userData?.profile_pic : userData?.teacher?.profile_pic,
                    userRole,
                    String(recipientId),
                    `${recipientData.first_name ?? ""} ${recipientData.last_name ?? ""}`.trim(),
                    recipientData.profile_image || recipientData.teacher_profile_image,
                    role === 1 ? 2 : 1
                );

                setActiveConversationId(conversationId);
                setActiveChatData((prev) =>
                    prev ? {...prev, firestore_conversation_id: conversationId} : prev
                );

                // Patch the chat list in cache so this row now has the convo id
                queryClient.setQueryData(
                    ["getChatListing", role, currentUserId, activeId],
                    (old) => {
                        if (!old?.data) return old;
                        const patched = old.data.map((channel) =>
                            String(channel.id) === String(activeChatData.id)
                                ? {...channel, firestore_conversation_id: conversationId}
                                : channel
                        );
                        return {...old, data: patched};
                    }
                );
            } catch (error) {
                console.error("Error creating conversation:", error);
                return;
            }
        }

        try {
            await sendMessage(
                messageText,
                conversationId,
                String(currentUserId ?? ""),
                String(recipientId),
                `${activeChatData?.userData?.first_name ?? ""} ${activeChatData?.userData?.last_name ?? ""}`.trim()
            );
        } catch (error) {
            console.error("Error sending message:", error);
        }
    };


    useEffect(() => {
        if (!chatList || chatList.length === 0) return;

        if (activeId) {
            const targetChat = chatList.find((chat) => {
                const participants = Object.values(chat.participants || {});
                return (
                    participants.includes(activeId) ||
                    chat.userData?.teacher_user_id === activeId ||
                    chat.id === activeId
                );
            });

            if (targetChat && isValidChat(targetChat)) {
                handleSelectConversation(targetChat);
                return;
            }
        }

        if (!activeChatData) {
            const validChat = chatList.find((chat) => isValidChat(chat));
            if (validChat) {
                handleSelectConversation(validChat);
            }
        }
    }, [chatList, activeChatData, activeId]);

    useEffect(() => {
        if (!activeConversationId) {
            setChatMessages([]);
            return;
        }

        if (!chatList || !currentUserId) {
            return;
        }

        const selectedChat = chatList?.find(
            (chat) => chat.firestore_conversation_id === activeConversationId,
        );

        if (!selectedChat || !isValidChat(selectedChat)) {
            return;
        }

        const recipientId =
            selectedChat?.participants &&
            Object.values(selectedChat?.participants).find(
                (participant) => participant !== currentUserId,
            );

        if (!recipientId) {
            return;
        }
        setIsLoadingChat(true);

        const unsubscribe = getMessages(
            activeConversationId,
            (messagesCallback) => {
                setChatMessages(
                    messagesCallback.map((message) => ({
                        _id: message.id,
                        text: message.text,
                        createdAt: convertTimestamp(message.timestamp),
                        user: {_id: message.senderId},
                    })),
                );
                setIsLoadingChat(false);
            },
            (err) => {
                setIsLoadingChat(false);
                console.error("onSnapshot error:", err);
            }
        );

        markMessagesAsRead(activeConversationId, String(currentUserId));

        return () => {
            unsubscribe();
            markMessagesAsRead(activeConversationId, String(currentUserId));
            updateTypingStatus(activeConversationId, currentUserId, false);
        };
    }, [activeConversationId, chatList, currentUserId]);

    const validChatList = chatList?.filter((chat) => isValidChat(chat)) || [];

    return {
        chatMessages,
        chatList: validChatList,
        activeConversation,
        handleSelectConversation,
        handleSendMessage,
        isLoading,
        isLoadingChat,
    };
};